import PageTitle from '@/components/Admin/PageTitle';
import Table from '@/components/Admin/Table';
import React from 'react';
import styled from 'styled-components';
import { useUIStore } from '@/store/useUIStore';
import { DropdownActionType } from '@/types/shared';
import { useQuery } from '@tanstack/react-query';
import { fetchStores, deleteStore } from '@/api/store';
import { colors } from '@/constants/colors';
import { toast } from 'react-toastify';

interface StoreSettingsType {
   btnColor: string;
   btnLabel: string;
   btnEmbed: string;
   designTheme: string;
   colorTheme: string;
   imgs: string[];
   slidesEnabled?: boolean;
   slidesHeight?: number;
   portalIp?: string;
}

export type TableStore = {
   _id: string;
   storename: string;
   description?: string;
   location: string;
   logo?: string;
   buttonLabel: string;
   storeDomain: string;
   availableVouchers?: number;
   soldVouchers?: number;
   totalSales?: number;
   settings: StoreSettingsType;
};

export type PageTitleActions = {
   type: 'normal' | 'button';
   title: string;
   onClick?: () => void;
};

const Stores = () => {
   // const { refresh } = useUIStore();
   const { refresh, handleShow, handleRefresh, handleClose } = useUIStore();

   const {
      data: response,
      isLoading,
      error,
   } = useQuery({
      queryKey: ['stores-list', refresh],
      queryFn: fetchStores,
   });

   // console.log(`response=====>`, response);

   // Transform the API data to match the table format
   const transformedData =
      response?.result?.map((store: TableStore) => ({
         ...store,
         availableVouchers: store.availableVouchers || 0,
         soldVouchers: store.soldVouchers || 0,
         totalSales: store.totalSales || 0,
         btnColor: store.settings.btnColor || '#B3D946',
         btnLabel: store.settings.btnLabel || 'Pay via Gcash',
         btnEmbed:
            store.settings.btnEmbed ||
            `<a href="${
               store.storeDomain
            }" style="text-decoration: none;"><button style="background-color: ${
               store.settings.btnColor || '#B3D946'
            }; outline: none; border: none; width: 100%; height: 40px; border-radius: 4px; color: white; margin-top: 2px;">${
               store.settings.btnLabel || 'Pay via Gcash'
            }</button></a>`,
      })) || [];

   // console.log(transformedData);

   const openSetup = (store: TableStore) => {
      handleShow({
         template: 'update-store',
         title: `Update - ${store.storename}`,
         windowType: 'desktop',
         data: {
            _id: store._id,
            storename: store.storename,
            description: store.description,
            location: store.location,
            logo: store.logo,
            storeDomain: store.storeDomain,
            settings: { ...store.settings },
         },
      });
   };

   const storeHeadData = [
      {
         type: 'empty',
         value: '',
      },
      {
         type: 'title',
         value: 'Store Name',
      },
      {
         type: 'title',
         value: 'Store Domain',
      },
      {
         type: 'title',
         value: 'Available',
      },
      {
         type: 'title',
         value: 'Sold',
      },
      {
         type: 'title',
         value: 'Total Sales',
      },
      {
         type: 'empty',
         value: '',
      },
      {
         type: 'title',
         value: 'Action',
      },
   ];

   const pageTitleActions: PageTitleActions[] = [
      {
         type: 'button',
         title: 'Create Store',
         onClick: () => {
            handleShow({
               template: 'create-store',
               title: 'Create Store',
               windowType: 'desktop',
               data: null,
            });
         },
      },
   ];

   const dropdownActions: DropdownActionType<TableStore>[] = [
      {
         label: 'Edit',
         onClick: (data: TableStore) => {
            // console.log(`----`,data)

            const storeValues = {
               _id: data._id,
               storename: data.storename,
               location: data.location,
               storeDomain: data.storeDomain,
               settings: {
                  ...data.settings,
                  // btnColor: data.settings.btnColor,
                  // btnLabel: data.settings.btnLabel,
                  // btnEmbed: data.settings.btnEmbed,
               },
            };

            handleShow({
               template: 'update-store',
               title: `Update - ${data.storename}`,
               windowType: 'desktop',
               data: storeValues,
            });
         },
         icon: 'edit-2-line',
      },
      {
         label: 'Delete',
         onClick: (data: TableStore) => {
            // console.log('Delete', data);

            handleShow({
               template: 'verification',
               title: `Delete Confirmation - ${data.storename}`,
               windowType: 'desktop',
               data: {
                  data: data,
                  msg: 'Do you want to delete this store? This action cannot be undone.',
               },
               actions: {
                  onConfirm: async (store: unknown) => {
                     try {
                        // console.log('Confirm---', store);
                        const typedStore = store as TableStore;
                        const res = await deleteStore(typedStore._id);
                        toast.success(res.data.msg, {
                           position: 'top-right',
                           autoClose: 2500,
                           hideProgressBar: false,
                           closeOnClick: true,
                           pauseOnHover: true,
                        });
                        handleClose();
                        handleRefresh();
                     } catch (err) {
                        console.log('Something went wrong', err);
                        toast.error('Something went wrong', {
                           position: 'top-right',
                           autoClose: 2500,
                           hideProgressBar: false,
                           closeOnClick: true,
                           pauseOnHover: true,
                           draggable: true,
                        });
                     }
                  },
                  onCancel: () => {
                     console.log('Cancel');
                     // handleClose();
                  },
               },
            });
         },
         icon: 'delete-bin-6-line',
      },
   ];

   // console.log(response?.result)

   // console.log('Transformed data:', transformedData);

   return (
      <Wrapper>
         <PageTitle title="Stores" actions={pageTitleActions} />
         {isLoading ? (
            <LoadingText>Loading stores...</LoadingText>
         ) : error ? (
            <ErrorText>Error loading stores: {error.message}</ErrorText>
         ) : (
            <Table<TableStore>
               headData={storeHeadData}
               data={transformedData}
               grid={`40px .5fr 1fr 80px 80px 100px 70px auto`}
               type="store"
               dropdownData={dropdownActions}
               onSetup={openSetup}
            />
         )}
      </Wrapper>
   );
};

const Wrapper = styled.div`
   /* font-family: 'Inter', sans-serif; */
`;

const LoadingText = styled.div`
   text-align: center;
   padding: 2rem;
   color: ${colors.GRAY5};
`;

const ErrorText = styled.div`
   text-align: center;
   padding: 2rem;
   color: ${colors.DANGER};
`;

export default Stores;
